import {
	ChangeDetectionStrategy,
	Component,
	signal,
	computed,
	effect,
	input,
	OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';

import {
	ClientFeatures,
	Gallery,
	Product,
	ProductEnum,
} from '@uc/web/shared/data-models';
import { CourseOption, UniversityCourse } from '../../models/course.models';
import { fetchUniversity, fetchCourse } from '../../data-access/+state/course.actions';
import {
	getStateCourse,
	getStateCourseIsLoading,
	getStateUniIsLoading,
	getStateUniversity,
} from '../../data-access/+state/course.selectors';
import { CourseService } from '../../services/course.service';
import {
	CtaNonClearingComponent,
	UNI_SLIDER_COPY,
	UniversitySliderComponent,
} from '@uc/web/shared/feature/feature-university-slider';
import {
	fetchAllEvents,
	getPostgraduateEventsData,
	getUndergraduateEventsData,
} from '@uc/web/shared/data-access';
import { ClearingService, RemoveDuplicateDomElementDirective } from '@uc/utilities';
import {
	abbreviateProduct,
	ClientValidatorService,
	getDetailByProduct,
	RequestInfoGTMService,
} from '@uc/web/core';
import {
	CourseSearchBannerComponent,
	EngageCardCtaComponent,
	EngageCardProspectusCtaComponent,
	RequestBtnComponent,
	RequestBtnService,
	UniversityEventsComponent,
	UniversityHeaderComponent,
	VisitWebsiteBtnComponent,
} from '@web/shared/ui';
import { CourseAnalyticsService } from '../../services/course-analytics.service';
import { HeaderTitleComponent } from '../../components/header-title/header-title.component';
import { CourseInformationComponent } from '../../components/course-information/course-information.component';
import { CourseProfileApplicationDetailsComponent } from '../../components/course-profile-application-details/course-profile-application-details.component';
import { EntryRequirementsComponent } from '../../components/entry-requirements/entry-requirements.component';
import { FeesAndFundingComponent } from '../../components/fees-and-funding/fees-and-funding.component';
import { NoFeesAndFundingComponent } from '../../components/no-fees-and-funding/no-fees-and-funding.component';
import { CourseSkeletonComponent } from '../../components/course-skeleton/course-skeleton.component';
import { HeadingComponent } from '@uc/shared/ui';
import {
	ClientProfileStickyLeadBarDesktopComponent,
	ClientProfileStickyLeadBarMobileComponent,
} from '@web/shared/feature/feature-sticky-lead-bar';
import { RecommendedUnisCarouselComponent } from '@uc/web/shared/feature/feature-recommended-universities';

@Component({
	selector: 'uc-course-details',
	templateUrl: './course-details.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		RemoveDuplicateDomElementDirective,
		UniversityHeaderComponent,
		HeaderTitleComponent,
		HeadingComponent,
		VisitWebsiteBtnComponent,
		CourseInformationComponent,
		RequestBtnComponent,
		CourseProfileApplicationDetailsComponent,
		EntryRequirementsComponent,
		CourseSearchBannerComponent,
		UniversityEventsComponent,
		EngageCardCtaComponent,
		FeesAndFundingComponent,
		NoFeesAndFundingComponent,
		EngageCardProspectusCtaComponent,
		ClientProfileStickyLeadBarDesktopComponent,
		CourseSkeletonComponent,
		UniversitySliderComponent,
		CtaNonClearingComponent,
		RecommendedUnisCarouselComponent,
		ClientProfileStickyLeadBarMobileComponent,
	],
})
export class CourseDetailsComponent implements OnInit {
	uniSlug = input.required<string>();
	studyLevel = input.required<Product>();
	courseSlug = input.required<string>();
	preview = input<string>();

	selectedIndex = signal<number>(0);

	externalUrl = computed(() => this.getExternalUrl(this.university()));

	externalEventsUrl = computed(() => this.getExternalEventsUrl(this.university()));

	externalProspectusUrl = computed(() =>
		this.getExternalProspectusUrl(this.university()),
	);

	isPreview = computed(() => this.preview() === 'true');

	isFeatured = computed(() =>
		this._cvSrv.checkFeatureBasedOnProduct(
			this.university()?.paid_features,
			this.studyLevel(),
			this.isPreview(),
		),
	);

	isFeaturedGallery = computed(() =>
		this._cvSrv.checkFeature(
			this.university()?.paid_features,
			this.paidFeaturesGallery,
		),
	);

	description = computed(() => {
		const universityName = this.university()?.name ?? '';
		return this._courseSrv.getCourseSearchBannerDesc(
			universityName,
			this.studyLevel(),
		);
	});

	uniSliderCopy = computed(() => this.setUniSliderCopy());

	events = computed(() => {
		const events = this._store.selectSignal(this.selectAction(this.studyLevel()));
		return events();
	});

	uniGallery = computed(() => {
		const university = this.university();
		if (!university) return;

		const gallery: Gallery = {
			cover_image: university.undergraduate.cover_image,
			images: university.universityGallery.images,
		};
		return gallery;
	});

	abbreviatedProduct = computed(() => abbreviateProduct(this.studyLevel()));

	university = this._store.selectSignal(getStateUniversity);

	course = this._store.selectSignal(getStateCourse);

	isCourseLoading = this._store.selectSignal(getStateCourseIsLoading);
	isUniLoading = this._store.selectSignal(getStateUniIsLoading);
	isLoading = computed(() => this.isCourseLoading() || this.isUniLoading());

	readonly paidFeaturesGallery = [
		ClientFeatures.Postgraduate,
		ClientFeatures.Undergraduate,
	];

	currentClearingYear = this._clearingSrv.getYear();
	clearingCourseEnabled = false;

	constructor(
		private _store: Store,
		private _clearingSrv: ClearingService,
		private _requestInfoGTMSrv: RequestInfoGTMService,
		private _requestBtnSrv: RequestBtnService,
		private _courseAnalyticsSrv: CourseAnalyticsService,
		private _courseSrv: CourseService,
		private _cvSrv: ClientValidatorService,
	) {
		effect(() => {
			const course = this.course();
			const uni = this.university();
			if (!course || !uni) return;

			this._courseSrv.updateSeoMetadata(course.name, uni.name);

			this._courseAnalyticsSrv.executeGTMEvents(
				uni.id,
				uni.name,
				uni.order,
				course.id,
				course.name,
				this.studyLevel(),
			);

			if (this.preview()) {
				// Display the preview view only if the preview query param is present in the url
				this.paidFeaturesGallery.push(
					ClientFeatures.UnderPreview,
					ClientFeatures.PostPreview,
				);
			}
		});
	}

	ngOnInit(): void {
		this._store.dispatch(
			fetchUniversity({
				uniSlug: this.uniSlug(),
				product: this.studyLevel(),
			}),
		);

		this._store.dispatch(fetchCourse({ courseSlug: this.courseSlug() }));

		this._store.dispatch(
			fetchAllEvents({
				uniSlug: this.uniSlug(),
			}),
		);
	}

	onCourseOptionSelect(index: number) {
		this.selectedIndex.set(index);
	}

	getExternalUrl(university: UniversityCourse | null) {
		if (!university) return '';

		return getDetailByProduct(university, 'external_url', this.studyLevel());
	}

	getExternalEventsUrl(university: UniversityCourse | null) {
		if (!university) return '';

		return getDetailByProduct(university, 'external_events_url', this.studyLevel());
	}

	getExternalProspectusUrl(university: UniversityCourse | null) {
		if (!university) return '';

		return getDetailByProduct(
			university,
			'external_prospectus_url',
			this.studyLevel(),
		);
	}

	setUniSliderCopy() {
		if (this.isFeatured()) {
			if (this.studyLevel() === ProductEnum.Undergraduate) {
				return { ...UNI_SLIDER_COPY.course.undergraduate };
			}

			return { ...UNI_SLIDER_COPY.course.postgraduate };
		}

		return { ...UNI_SLIDER_COPY.course.nonClient };
	}

	isArray(element: any) {
		return Array.isArray(element);
	}

	hasFees(courseOption: CourseOption) {
		return (
			this.isArray(courseOption.fees) &&
			courseOption.fees &&
			courseOption.fees.length >= 1
		);
	}

	hasEntryRequirements(courseOption: CourseOption) {
		return (
			this.isArray(courseOption.entry_requirements) &&
			courseOption.entry_requirements &&
			courseOption.entry_requirements.length >= 1
		);
	}

	selectAction(studyLevel: Product) {
		if (studyLevel === ProductEnum.Postgraduate) {
			return getPostgraduateEventsData;
		} else {
			return getUndergraduateEventsData;
		}
	}

	onRequestInfoClick(ugSearchOrderPosition: number | null, courseName: string) {
		this._requestInfoGTMSrv.requestInfoClick(
			'Request Information CTA',
			this.studyLevel() === ProductEnum.Undergraduate
				? ugSearchOrderPosition
				: null,
		);
		this._requestBtnSrv.setCourseName(courseName);
	}
}
